/**
 * useLogin — sequences the two-call login and commits the session.
 *
 * The order here is the whole point, and it is NOT legacy's order:
 *
 *   1. POST /login
 *   2. CHECK the result            <- legacy does this at step 3
 *   3. POST /getUserToken
 *   4. commit token + identity to the store
 *   5. navigate
 *
 * Legacy runs steps 1 and 3 back to back and only then checks (Login.vue:137
 * vs :138). On a wrong password that sends `accessToken: Bearer undefined`,
 * which throws, and the throw is swallowed by `catch (e) { }` at Login.vue:200
 * — so a failed login shows the user absolutely nothing. Checking first is what
 * makes the error message reachable.
 *
 * Also not reproduced: the success snackbar and dashboard redirect at
 * Login.vue:192-199 sit outside the if/else and fire on the failure branches
 * too, and the trailing `window.location.reload()` at :190 — needed there only
 * because axios captured the token at module load. apiClient reads the token
 * per request, so nothing needs reloading.
 */
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { authenticated, authenticating, signedOut } from '@security/auth/authSlice';
import { readTokenClaims } from '@security/auth/jwt';
import { saveSession } from '@security/auth/sessionMirror';
import { setAccessToken } from '@security/auth/tokenStore';
import { useAppMode } from '@variant/useAppMode';

import { LOGIN_ERROR_CODE, useGetUserTokenMutation, useLoginMutation } from './api/authApi';
import { resolveRoles } from './roles';

/** Fallback when the server rejects without saying why. */
const GENERIC_FAILURE = 'Unable to sign in. Please check your credentials and try again.';

/**
 * Pull a human message out of whatever the transport produced.
 *
 * interceptors.js already normalises HTTP failures onto `error.normalized`;
 * this adds the 200-with-ErrorCode case, which no interceptor can see.
 */
function messageFrom(error) {
  return (
    error?.normalized?.message || error?.data?.Message || error?.data?.message || GENERIC_FAILURE
  );
}

export function useLogin() {
  const dispatch = useDispatch();
  const { mode, variant } = useAppMode();

  const [login, loginState] = useLoginMutation();
  const [getUserToken, userState] = useGetUserTokenMutation();
  const [error, setError] = useState(null);

  const submit = useCallback(
    async ({ email, password }) => {
      setError(null);
      dispatch(authenticating());

      try {
        // --- 1. credentials -> jwtToken -------------------------------------
        const result = await login({ email, password }).unwrap();

        // --- 2. check BEFORE going further ----------------------------------
        // A failed sign-in arrives as HTTP 200 with ErrorCode: -1, so this is
        // not redundant with the catch below — nothing threw.
        if (result?.ErrorCode === LOGIN_ERROR_CODE) {
          dispatch(signedOut());
          setError(result.Message || GENERIC_FAILURE);
          return false;
        }

        const jwtToken = result?.jwtToken;
        if (!jwtToken) {
          dispatch(signedOut());
          setError(result?.Message || GENERIC_FAILURE);
          return false;
        }

        // --- 3. jwtToken -> identity + roles --------------------------------
        const data = await getUserToken({
          jwtToken,
          application: variant.shortName, // 'CCA' | 'EIA'
        }).unwrap();

        const { roles, features } = resolveRoles(data?.FeaturesData, mode);
        const user = {
          email: data?.UserData?.userEmail ?? email,
          firstName: data?.UserData?.firstName ?? '',
          lastName: data?.UserData?.lastName ?? '',
          org: data?.UserData?.org ?? '',
        };

        // --- 4. commit ------------------------------------------------------
        // Identity first, then the token: setAccessToken re-writes the jwt_token
        // cookie, so the mirror has to already hold the identity it belongs to.
        // The RAW payload goes to the mirror, not the narrowed `user`/`features`
        // above: the legacy cookie set carries both products' roles, and the
        // narrowed form has already dropped the other variant's branch.
        const { expiresInSeconds } = readTokenClaims(jwtToken) ?? { expiresInSeconds: 0 };
        saveSession({
          token: jwtToken,
          userData: data?.UserData,
          featuresData: data?.FeaturesData,
          application: variant.shortName,
        });
        setAccessToken(jwtToken, expiresInSeconds);
        dispatch(authenticated({ user, roles, features }));

        return true;
      } catch (err) {
        // Nothing is swallowed. Any failure leaves the session explicitly
        // signed out rather than half-populated.
        dispatch(signedOut());
        setError(messageFrom(err));
        return false;
      }
    },
    [dispatch, login, getUserToken, mode, variant.shortName],
  );

  return {
    submit,
    error,
    clearError: useCallback(() => setError(null), []),
    isSubmitting: loginState.isLoading || userState.isLoading,
  };
}
